import { Recipe } from '../types/recipe';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const componentApiService = {
  /**
   * Fetch all components from the backend
   */
  fetchComponents: async (): Promise<Recipe[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/components`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch components');
      }

      return data.components;
    } catch (error) {
      console.error('Error fetching components from API:', error);
      throw error;
    }
  },

  /**
   * Create a new component
   */
  createComponent: async (component: Recipe): Promise<Recipe> => {
    try {
      const response = await fetch(`${API_BASE_URL}/components`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(component)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create component');
      }

      return data.component;
    } catch (error) {
      console.error('Error creating component:', error);
      throw error;
    }
  },

  /**
   * Update an existing component
   */
  updateComponent: async (component: Recipe): Promise<Recipe> => {
    try {
      const response = await fetch(`${API_BASE_URL}/components/${component.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(component)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update component');
      }

      return data.component;
    } catch (error) {
      console.error('Error updating component:', error);
      throw error;
    }
  },

  /**
   * Delete a component by ID
   */
  deleteComponent: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/components/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete component');
      }
    } catch (error) {
      console.error('Error deleting component:', error);
      throw error;
    }
  },

  /**
   * Fetch ingredient names for a specific component
   */
  fetchComponentIngredients: async (componentId: string): Promise<string[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/components/${componentId}/ingredients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch component ingredients: ${response.statusText}`);
      }

      const data = await response.json();
      return data.ingredients;
    } catch (error) {
      console.error('Error fetching component ingredients:', error);
      throw error;
    }
  },

  /**
   * Fetch component recipe (ingredients with quantities)
   */
  fetchComponentRecipe: async (componentId: string): Promise<{
    ingredients: { ingredientName: string; quantity: number; unit: string }[];
  }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/components/${componentId}/recipe`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch component recipe');
      }

      return {
        ingredients: data.ingredients,
      };
    } catch (error) {
      console.error('Error fetching component recipe from API:', error);
      throw error;
    }
  }
};
